import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Plus, Trash2, Tag, Calendar, CheckCircle, Clock, XCircle,
  Award, Percent
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  DRAFT: { label: "Bozza", icon: Clock, color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
  ACTIVE: { label: "Attiva", icon: CheckCircle, color: "bg-green-500/20 text-green-400 border-green-500/30" },
  ENDED: { label: "Terminata", icon: XCircle, color: "bg-red-500/20 text-red-400 border-red-500/30" }
};

const ruleTypeLabels = {
  POINTS_PER_EURO: "Punti per Euro",
  POINTS_PER_ITEM: "Punti per Articolo"
};

export default function AdminCampaignDetail({ user }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  
  // Rule form
  const [ruleForm, setRuleForm] = useState({
    brand: "",
    rule_type: "POINTS_PER_ITEM",
    value: ""
  });

  useEffect(() => {
    fetchCampaignDetail();
  }, [campaignId]);

  const fetchCampaignDetail = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API}/admin/campaigns/${campaignId}`, { 
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaign(data.campaign);
        setRules(data.rules);
      } else {
        toast.error("Campagna non trovata");
        navigate("/admin/campaigns");
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!ruleForm.brand || !ruleForm.value) {
      toast.error("Compila tutti i campi");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/campaigns/${campaignId}/rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brand: ruleForm.brand,
          rule_type: ruleForm.rule_type,
          value: parseFloat(ruleForm.value)
        })
      });

      if (response.ok) {
        toast.success("Regola aggiunta");
        setRuleDialogOpen(false);
        setRuleForm({ brand: "", rule_type: "POINTS_PER_ITEM", value: "" });
        fetchCampaignDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm("Eliminare questa regola?")) return;

    try {
      const response = await authFetch(`${API}/admin/campaigns/${campaignId}/rules/${ruleId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Regola eliminata");
        fetchCampaignDetail();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!campaign) return null;

  const StatusIcon = statusConfig[campaign.status]?.icon || Clock;
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/campaigns")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="page-title">{campaign.name}</h1>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${statusConfig[campaign.status]?.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig[campaign.status]?.label}
            </span>
          </div>
          {campaign.description && (
            <div className="text-zinc-500 mt-1">{campaign.description}</div>
          )}
        </div>
      </div>

      {/* Campaign Info */}
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Data Inizio</div>
            <div className="text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              {new Date(campaign.start_date).toLocaleDateString("it-IT")}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Data Fine</div>
            <div className="text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              {new Date(campaign.end_date).toLocaleDateString("it-IT")}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Regole Attive</div>
            <div className="text-white font-mono text-lg">{rules.length}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Creata il</div>
            <div className="text-white">
              {new Date(campaign.created_at).toLocaleDateString("it-IT")}
            </div>
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            Regole Accumulo Punti
          </h2>
          
          {isAdmin && (
            <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Aggiungi Regola
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>Nuova Regola</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-zinc-400">Marca *</label>
                    <Input
                      value={ruleForm.brand}
                      onChange={(e) => setRuleForm({...ruleForm, brand: e.target.value})}
                      placeholder="es. BOSCH, BREMBO, NGK..."
                      className="bg-zinc-950 border-zinc-800 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Tipo Regola</label>
                    <Select 
                      value={ruleForm.rule_type} 
                      onValueChange={(v) => setRuleForm({...ruleForm, rule_type: v})}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="POINTS_PER_ITEM">Punti per Articolo</SelectItem>
                        <SelectItem value="POINTS_PER_EURO">Punti per Euro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">
                      Valore ({ruleForm.rule_type === "POINTS_PER_EURO" ? "punti per €" : "punti per articolo"}) *
                    </label>
                    <Input
                      type="number"
                      value={ruleForm.value}
                      onChange={(e) => setRuleForm({...ruleForm, value: e.target.value})}
                      placeholder="es. 10"
                      className="bg-zinc-950 border-zinc-800 mt-1"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setRuleDialogOpen(false)}>Annulla</Button>
                  <Button onClick={handleAddRule}>Aggiungi</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {rules.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <div>Nessuna regola definita</div>
            <div className="text-sm mt-1">Aggiungi regole per definire come vengono accumulati i punti</div>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <div 
                key={rule.rule_id}
                className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Tag className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-white font-medium">{rule.brand}</div>
                    <div className="text-sm text-zinc-400">
                      {ruleTypeLabels[rule.rule_type] || rule.rule_type}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary font-mono">{rule.value}</div>
                    <div className="text-xs text-zinc-500">
                      {rule.rule_type === "POINTS_PER_EURO" ? "punti/€" : "punti/articolo"}
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => handleDeleteRule(rule.rule_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
